// src/tenantAdmin/layout/TenantAdminLayout.jsx
//
// The chrome of "Gestione Workspace": the page title, the tab bar and the company
// the company tabs act on. Modelled on src/admin/AdminLayout.jsx (NavLink + a
// shared `tabStyle`, theme tokens only) so the two consoles read alike.
//
// ⛔ The five company tabs are HIDDEN for a non-company-admin — a DOCUMENTED
// exception to this repo's "role-gated entries are greyed, never hidden" rule
// (plan decision 7). The rule exists so a menu does not reshuffle by role; these
// are not menu entries but sections of DATA the user cannot read at all, and a
// greyed "Dati azienda" would name a company the user has no relationship with.
// src/__tests__/AppRoutes.test.jsx pins the absence, so re-adding them greyed fails.
//
// ⛔ The tabs are built from the ROLE rather than filtered from one list: "Utenti"
// needs an administered workspace (`adminTenants`), the other five need a
// company claim (`isCustomerAdmin`), and the two rights are independent — a
// company admin may administer no workspace row of their own, and a workspace
// admin usually has no company claim.
import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext.jsx';
import useManagedCompany from '../hooks/useManagedCompany.js';
import PersonalizationBadge from '../../onboarding/ui/PersonalizationBadge.jsx';
import PageBand, { PAGE_BAND_TAB } from '../../components/ui/PageBand.jsx';
import { COLORS, FONT_SANS, RADII } from '../../constants/theme.js';

export default function TenantAdminLayout() {
  const { t } = useTranslation('admin');
  const { adminTenants, isCustomerAdmin } = useAuth();
  // ⛔ Called ONCE, here: the default company costs a /credits/balance read, and a
  // per-tab hook would repeat it on every tab change. The tabs below read it from
  // the Outlet context (`useOutletContext().company.companyId`).
  const company = useManagedCompany();

  // Defensive: test mocks (and any consumer) may omit it.
  const hasAdminWorkspace = (adminTenants || []).length > 0;

  const tabs = [
    ...(hasAdminWorkspace ? [{ key: 'users', path: '/tenant-admin/manage/users' }] : []),
    ...(isCustomerAdmin ? [
      { key: 'workspaces', path: '/tenant-admin/manage/workspaces' },
      { key: 'company', path: '/tenant-admin/manage/company' },
      { key: 'credits', path: '/tenant-admin/manage/credits' },
      { key: 'plans', path: '/tenant-admin/manage/plans' },
    ] : []),
  ];

  return (
    <div>
      {/* The white band (components/ui/PageBand.jsx, shared with Knowledge Base, Brand Voice and Buyer
          Personas): the title; at the far right the personalization badge (v191 — it describes the ACTIVE
          workspace and renders nothing when the user does not administer it; v201: moved there from beside the
          title) and then the company selector; the tabs underneath. */}
      <PageBand
        title={t('manage.title')}
        aside={<PersonalizationBadge />}
        action={company.options.length > 1 && (
          // The company the five company tabs act on. Rendered only when the user administers more
          // than one: with a single claim there is nothing to choose and a one-option select is noise.
          <label style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            fontFamily: FONT_SANS, fontSize: '13px', color: COLORS.textSecondary,
          }}>
            {t('manage.companySelector')}
            <select
              value={company.companyId || ''}
              onChange={(e) => company.setCompanyId(e.target.value)}
              style={{
                height: '36px', padding: '0 10px', borderRadius: RADII.control,
                border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                fontFamily: FONT_SANS, fontSize: '13px', color: COLORS.ink,
              }}
            >
              {company.options.map(c => (
                <option key={c.customer_id} value={c.customer_id}>
                  {c.legal_name || c.customer_id}
                </option>
              ))}
            </select>
          </label>
        )}
      >
        {tabs.map(tab => (
          <NavLink key={tab.path} to={tab.path} className={PAGE_BAND_TAB}>
            {t(`manage.tabs.${tab.key}`)}
          </NavLink>
        ))}
      </PageBand>

      <Outlet context={{ company }} />
    </div>
  );
}
